import type { Lesson } from "@/core/types";
import { formelAufgabe } from "../formelaufgabe";

export const lektionTextfunktionen: Lesson = {
  id: "textfunktionen",
  title: "Textfunktionen: zerlegen, bereinigen, verbinden",
  summary: "Namen trennen, Leerzeichen entfernen, Texte zusammensetzen – per Formel statt von Hand.",
  durationMinutes: 13,
  learningGoals: [
    "Texte mit LINKS, RECHTS, TEIL oder TEXTVOR/TEXTNACH zerlegen",
    "Die Trennstelle mit FINDEN oder SUCHEN ermitteln statt abzuzählen",
    "Importierte Texte mit GLÄTTEN und GROSS2 bereinigen",
    "Texte mit & und TEXTVERKETTEN zusammensetzen",
  ],
  steps: [
    {
      id: "zerlegen",
      title: "Texte zerlegen",
      explanation: [
        "LINKS(Text;Anzahl) liefert die ersten Zeichen eines Textes, RECHTS die letzten, " +
          "TEIL(Text;Start;Anzahl) ein Stück aus der Mitte. =LINKS(A2;3) macht aus " +
          "„BER-2025-117“ das Kürzel „BER“.",
        "Meist ist die Länge aber nicht fest: Vornamen sind unterschiedlich lang. Dann " +
          "sucht FINDEN die Trennstelle: =LINKS(A2;FINDEN(\" \";A2)-1) liefert alles vor dem " +
          "ersten Leerzeichen. In Microsoft 365 geht es kürzer mit TEXTVOR und TEXTNACH: " +
          "=TEXTVOR(A2;\" \") und =TEXTNACH(A2;\" \").",
        "Eine Formel statt Abtippen hat den üblichen Vorteil: Kommen neue Namen dazu, " +
          "genügt Ausfüllen. Wer nur einmal trennen will, findet in der nächsten Lektion " +
          "„Blitzvorschau“ und „Text in Spalten“ – die liefern aber feste Werte.",
      ],
      keyPoints: [
        "LINKS, RECHTS, TEIL schneiden aus; FINDEN liefert die Position.",
        "=LINKS(A2;FINDEN(\" \";A2)-1) = alles vor dem ersten Leerzeichen.",
        "TEXTVOR / TEXTNACH (Microsoft 365) erledigen beides in einem.",
      ],
      task: {
        instruction:
          "Schreibe einige vollständige Namen („Anna Weber“) in eine Spalte und trenne den " +
          "Vornamen per Formel ab – mit einer gesuchten, nicht abgezählten Trennstelle.",
        hint: "=LINKS(A2;FINDEN(\" \";A2)-1) oder =TEXTVOR(A2;\" \") und nach unten ausfüllen.",
        check: formelAufgabe(
          [
            { label: "Eine Formel schneidet Text aus", funktionen: ["links", "rechts", "teil", "textvor", "textnach", "textteilen"] },
            {
              label: "Die Trennstelle wird gesucht, nicht abgezählt",
              funktionen: ["finden", "suchen", "textvor", "textnach", "textteilen"],
            },
          ],
          {
            success: "Die Namen trennen sich jetzt selbst – auch neue, die dazukommen.",
            failure: "Die Zerlegung ist noch nicht vollständig.",
            hint: "Eine feste Zahl wie =LINKS(A2;4) passt nur für Vornamen mit vier Buchstaben.",
          },
        ),
      },
    },
    {
      id: "bereinigen",
      title: "Importierte Texte bereinigen",
      explanation: [
        "Daten aus anderen Systemen bringen oft Ballast mit: doppelte Leerzeichen, " +
          "Leerzeichen am Ende, unsichtbare Steuerzeichen, GROSSBUCHSTABEN. Das Ergebnis: " +
          "SVERWEIS findet „Weber “ nicht, weil hinten ein Leerzeichen steht.",
        "GLÄTTEN entfernt Leerzeichen am Anfang und Ende und macht aus mehreren eines. " +
          "SÄUBERN entfernt nicht druckbare Zeichen. GROSS2 schreibt jedes Wort mit großem " +
          "Anfangsbuchstaben, KLEIN und GROSS wandeln ganz um.",
        "Der übliche Weg: Hilfsspalte mit =GLÄTTEN(A2), ausfüllen, dann kopieren und über " +
          "„Einfügen“ → „Werte“ zurück auf die ursprüngliche Spalte legen.",
      ],
      keyPoints: [
        "GLÄTTEN gegen überzählige Leerzeichen, SÄUBERN gegen Steuerzeichen.",
        "GROSS2 für Namen in Großbuchstaben.",
        "Hilfsspalte → kopieren → Werte einfügen.",
      ],
      task: {
        instruction: "Bereinige eine Textspalte mit GLÄTTEN, SÄUBERN oder GROSS2.",
        hint: "Schreibe „  anna   WEBER “ in eine Zelle und daneben =GROSS2(GLÄTTEN(A2)).",
        check: formelAufgabe(
          [{ label: "Eine Formel bereinigt Text", funktionen: ["glaetten", "saeubern", "gross2"] }],
          {
            success: "Sauberer Text – jetzt finden auch Verweisfunktionen, was sie suchen.",
            failure: "Es gibt noch keine Bereinigungsformel.",
          },
        ),
      },
    },
    {
      id: "verbinden",
      title: "Texte verbinden",
      explanation: [
        "Das Kaufmanns-Und verbindet Texte: =B2&\" \"&C2 macht aus Vor- und Nachname " +
          "wieder einen vollständigen Namen. Das Leerzeichen dazwischen muss man selbst als " +
          "Text einfügen.",
        "TEXTVERKETTEN verbindet ganze Bereiche mit einem Trennzeichen und überspringt auf " +
          "Wunsch leere Zellen: =TEXTVERKETTEN(\", \";WAHR;B2:B20) ergibt eine kommagetrennte " +
          "Liste aller Namen.",
        "Soll eine Zahl oder ein Datum im Text erscheinen, hilft TEXT: " +
          "=\"Stand: \"&TEXT(HEUTE();\"TT.MM.JJJJ\") – ohne TEXT stünde dort die Seriennummer " +
          "des Datums.",
      ],
      keyPoints: [
        "& verbindet, Leerzeichen als \" \" dazwischen.",
        "TEXTVERKETTEN(Trennzeichen;Leere ignorieren;Bereich).",
        "TEXT(Wert;Format) für Zahlen und Datumsangaben im Text.",
      ],
      task: {
        instruction: "Setze aus zwei Zellen per Formel einen Text zusammen, etwa Vor- und Nachname mit Leerzeichen.",
        hint: "=B2&\" \"&C2 oder =TEXTVERKETTEN(\" \";WAHR;B2:C2).",
        check: formelAufgabe(
          [
            {
              label: "Eine Formel verbindet Texte",
              test: (formel) =>
                /&/.test(formel.replace(/"[^"]*"/g, "")) ||
                /\b(TEXTVERKETTEN|TEXTJOIN|TEXTKETTE|CONCAT|VERKETTEN|CONCATENATE)\s*\(/i.test(formel),
            },
          ],
          {
            success: "Aus Einzelteilen wird wieder ein ganzer Text – und er ändert sich mit.",
            failure: "Es gibt noch keine Formel, die Texte verbindet.",
          },
        ),
      },
    },
  ],
};
